import React, { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import {
  Button,
  Checkbox,
  Empty,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  message,
} from "antd";
import { format } from "date-fns";

const API_BASE = "https://www.smart-lend.com/api";

const emptyCreateForm = {
  name: "",
  email: "",
  password: "",
  billing_group_id: null,
  lender_user_id: null,
};

const emptyEditForm = {
  name: "",
  email: "",
  password: "",
  billing_group_id: null,
  lender_user_id: null,
  is_active: true,
};

const emptyTransactionForm = {
  target_type: "user",
  portal_user_id: null,
  billing_group_id: null,
  tokens_amount: 1,
  is_free: false,
  description: "",
};

const emptyBillingGroupForm = {
  name: "",
  is_active: true,
  lender_user_ids: [],
};

const monthOptions = [
  { label: "All Months", value: "all" },
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 },
];

const sortOptions = [
  { label: "Newest First", value: "transacted_at:desc" },
  { label: "Oldest First", value: "transacted_at:asc" },
  { label: "Highest Value", value: "total_value:desc" },
  { label: "Lowest Value", value: "total_value:asc" },
  { label: "Most Tokens", value: "tokens_amount:desc" },
  { label: "Least Tokens", value: "tokens_amount:asc" },
];

const PortalUsersAdmin = () => {
  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [billingGroups, setBillingGroups] = useState([]);
  const [lenderAccounts, setLenderAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [overviewSummary, setOverviewSummary] = useState(null);
  const [transactionSummary, setTransactionSummary] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [transactionOpen, setTransactionOpen] = useState(false);
  const [billingGroupOpen, setBillingGroupOpen] = useState(false);
  const [editingBillingGroup, setEditingBillingGroup] = useState(null);
  const [transactionSubmitting, setTransactionSubmitting] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [transactionForm, setTransactionForm] = useState(emptyTransactionForm);
  const [billingGroupForm, setBillingGroupForm] = useState(emptyBillingGroupForm);
  const [transactionFilters, setTransactionFilters] = useState({
    search: "",
    portal_user_id: "all",
    billing_group_id: "all",
    balance_scope: "all",
    transaction_type: "all",
    month: "all",
    year: "all",
    sort: "transacted_at:desc",
  });

  const authHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${Cookies.get("token")}`,
    }),
    [],
  );

  const selectedPeriodLabel = useMemo(() => {
    const hasPeriodFilter =
      transactionFilters.month !== "all" || transactionFilters.year !== "all";
    const hasOtherFilter =
      transactionFilters.search.trim() !== "" ||
      transactionFilters.portal_user_id !== "all" ||
      transactionFilters.transaction_type !== "all";

    if (hasPeriodFilter) return "Selected Period";
    if (hasOtherFilter) return "Filtered Result";
    return "All Transactions";
  }, [transactionFilters]);

  const tokensAmount = Number(transactionForm.tokens_amount || 0);
  const selectedTransactionTarget =
    transactionForm.target_type === "group"
      ? billingGroups.find((group) => group.id === transactionForm.billing_group_id)
      : users.find((user) => user.id === transactionForm.portal_user_id);
  const selectedLifetimePaidTokens = Number(
    selectedTransactionTarget?.total_paid_tokens ??
      selectedTransactionTarget?.total_tokens_granted ??
      0,
  );
  const pricingPreview = transactionForm.is_free
    ? { totalValue: 0, valuePerToken: 0 }
    : calculateTokenPurchaseValue(selectedLifetimePaidTokens, tokensAmount);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${API_BASE}/admin/bank-statement-portal-users`, {
        method: "GET",
        headers: authHeaders,
      });
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "Unable to load portal users.");
      }

      setUsers(json.users || []);
      setBillingGroups(json.billing_groups || []);
      setLenderAccounts(json.lender_accounts || []);
      setOverviewSummary(json.summary || null);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadTransactions = async (filters = transactionFilters) => {
    setLoadingTransactions(true);
    try {
      const params = new URLSearchParams();
      if (filters.search.trim()) params.set("search", filters.search.trim());
      if (filters.portal_user_id !== "all") {
        params.set("portal_user_id", String(filters.portal_user_id));
      }
      if (filters.billing_group_id !== "all") {
        params.set("billing_group_id", String(filters.billing_group_id));
      }
      if (filters.balance_scope !== "all") {
        params.set("balance_scope", filters.balance_scope);
      }
      if (filters.transaction_type !== "all") {
        params.set("transaction_type", filters.transaction_type);
      }
      if (filters.month !== "all") params.set("month", String(filters.month));
      if (filters.year !== "all") params.set("year", String(filters.year));

      const [sortBy, sortDirection] = String(filters.sort || "transacted_at:desc").split(":");
      params.set("sort_by", sortBy);
      params.set("sort_direction", sortDirection || "desc");

      const response = await fetch(
        `${API_BASE}/admin/bank-statement-portal-token-transactions?${params.toString()}`,
        {
          method: "GET",
          headers: authHeaders,
        },
      );
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "Unable to load token transactions.");
      }

      setTransactions(json.transactions || []);
      setTransactionSummary(json.summary || null);
      setAvailableYears(json.available_years || []);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoadingTransactions(false);
    }
  };

  useEffect(() => {
    loadUsers();
    loadTransactions();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadTransactions(transactionFilters);
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [transactionFilters]);

  const resetCreateForm = () => {
    setCreateForm(emptyCreateForm);
  };

  const resetTransactionForm = () => {
    setTransactionForm(emptyTransactionForm);
  };

  const openTransactionModal = (user = null) => {
    setTransactionForm({
      ...emptyTransactionForm,
      target_type: "user",
      portal_user_id: user?.id ?? null,
    });
    setTransactionOpen(true);
  };

  const openGroupTransactionModal = (group = null) => {
    setTransactionForm({
      ...emptyTransactionForm,
      target_type: "group",
      billing_group_id: group?.id ?? null,
    });
    setTransactionOpen(true);
  };

  const createUser = async () => {
    try {
      const response = await fetch(`${API_BASE}/admin/bank-statement-portal-users`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          name: createForm.name.trim(),
          email: createForm.email.trim(),
          password: createForm.password,
          billing_group_id: createForm.billing_group_id || null,
          lender_user_id: createForm.lender_user_id || null,
        }),
      });
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "Unable to create portal user.");
      }

      message.success("Portal user created.");
      setCreateOpen(false);
      resetCreateForm();
      await loadUsers();
    } catch (error) {
      message.error(error.message);
    }
  };

  const updateUser = async () => {
    if (!editingUser) return;

    try {
      const payload = {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        is_active: editForm.is_active,
        billing_group_id: editForm.billing_group_id || null,
        lender_user_id: editForm.lender_user_id || null,
      };

      if (editForm.password.trim()) {
        payload.password = editForm.password;
      }

      const response = await fetch(
        `${API_BASE}/admin/bank-statement-portal-users/${editingUser.id}`,
        {
          method: "PUT",
          headers: authHeaders,
          body: JSON.stringify(payload),
        },
      );
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "Unable to update portal user.");
      }

      message.success("Portal user updated.");
      setEditingUser(null);
      await loadUsers();
    } catch (error) {
      message.error(error.message);
    }
  };

  const createTokenTransaction = async () => {
    if (transactionForm.target_type === "user" && !transactionForm.portal_user_id) {
      message.error("Select a portal user first.");
      return;
    }

    if (transactionForm.target_type === "group" && !transactionForm.billing_group_id) {
      message.error("Select a billing group first.");
      return;
    }

    setTransactionSubmitting(true);
    try {
      const body = {
        tokens_amount: Number(transactionForm.tokens_amount || 0),
        is_free: !!transactionForm.is_free,
        description: transactionForm.description.trim() || undefined,
      };

      if (transactionForm.target_type === "group" && transactionForm.portal_user_id) {
        body.portal_user_id = transactionForm.portal_user_id;
      }

      const url =
        transactionForm.target_type === "group"
          ? `${API_BASE}/admin/bank-statement-portal-billing-groups/${transactionForm.billing_group_id}/token-transactions`
          : `${API_BASE}/admin/bank-statement-portal-users/${transactionForm.portal_user_id}/token-transactions`;

      const response = await fetch(
        url,
        {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify(body),
        },
      );
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || firstValidationError(json) || "Unable to record the token transaction.");
      }

      message.success("Token transaction recorded.");
      setTransactionOpen(false);
      resetTransactionForm();
      await Promise.all([loadUsers(), loadTransactions()]);
      setActiveTab("transactions");
    } catch (error) {
      message.error(error.message);
    } finally {
      setTransactionSubmitting(false);
    }
  };

  const deleteUser = async (user) => {
    Modal.confirm({
      title: `Delete ${user.name}?`,
      content: "This removes the portal account. Existing transaction history will be deleted with the account.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const response = await fetch(
            `${API_BASE}/admin/bank-statement-portal-users/${user.id}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${Cookies.get("token")}`,
              },
            },
          );
          const json = await response.json();
          if (!response.ok) {
            throw new Error(json?.message || "Unable to delete portal user.");
          }

          message.success("Portal user deleted.");
          await Promise.all([loadUsers(), loadTransactions()]);
        } catch (error) {
          message.error(error.message);
        }
      },
    });
  };

  const saveBillingGroup = async () => {
    try {
      const isEdit = !!editingBillingGroup;
      const response = await fetch(
        isEdit
          ? `${API_BASE}/admin/bank-statement-portal-billing-groups/${editingBillingGroup.id}`
          : `${API_BASE}/admin/bank-statement-portal-billing-groups`,
        {
          method: isEdit ? "PUT" : "POST",
          headers: authHeaders,
      body: JSON.stringify({
        name: billingGroupForm.name.trim(),
        is_active: billingGroupForm.is_active,
        lender_user_ids: billingGroupForm.lender_user_ids || [],
      }),
        },
      );
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || firstValidationError(json) || "Unable to save billing group.");
      }

      message.success(isEdit ? "Billing group updated." : "Billing group created.");
      setBillingGroupOpen(false);
      setEditingBillingGroup(null);
      setBillingGroupForm(emptyBillingGroupForm);
      await loadUsers();
    } catch (error) {
      message.error(error.message);
    }
  };

  const openBillingGroupModal = (group = null) => {
    setEditingBillingGroup(group);
    setBillingGroupForm(
      group
        ? {
            name: group.name || "",
            is_active: !!group.is_active,
            lender_user_ids: group.lender_user_ids || [],
          }
        : emptyBillingGroupForm,
    );
    setBillingGroupOpen(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.name || "",
      email: user.email || "",
      password: "",
      billing_group_id: user.billing_group_id || null,
      lender_user_id: user.lender_user_id || null,
      is_active: !!user.is_active,
    });
  };

  const userOptions = [
    { label: "All Users", value: "all" },
    ...users.map((user) => ({
      label: `${user.name} (${user.email})`,
      value: user.id,
    })),
  ];

  const billingGroupOptions = [
    { label: "No Billing Group", value: null },
    ...billingGroups.map((group) => ({
      label: `${group.name} (${group.tokens_remaining} tokens)`,
      value: group.id,
    })),
  ];

  const lenderAccountOptions = [
    { label: "No linked lender account", value: null },
    ...lenderAccounts.map((account) => ({
      label: `${account.name} (${account.email})`,
      value: account.user_id,
    })),
  ];

  const transactionBillingGroupOptions = [
    { label: "All Billing Groups", value: "all" },
    ...billingGroups.map((group) => ({
      label: group.name,
      value: group.id,
    })),
  ];

  const targetBillingGroupMembers = users.filter(
    (user) => user.billing_group_id === transactionForm.billing_group_id,
  );

  const transactionTypeOptions = [
    { label: "All Types", value: "all" },
    { label: "Credits", value: "credit" },
    { label: "Usage", value: "usage" },
  ];

  const userColumns = [
    {
      title: "User",
      dataIndex: "name",
      key: "name",
      render: (value, record) => (
        <div>
          <div style={{ fontWeight: 800, color: "#12304a" }}>{value}</div>
          <div style={{ fontSize: "12px", color: "#6b7280" }}>{record.email}</div>
        </div>
      ),
    },
    {
      title: "Available Tokens",
      dataIndex: "tokens_remaining",
      key: "tokens_remaining",
      width: 140,
      render: (value) => <strong style={{ color: "#0e5aa5" }}>{value}</strong>,
    },
    {
      title: "Token Source",
      key: "token_source",
      width: 180,
      render: (_, record) => (
        <MetricCell
          primary={record.token_balance_scope === "group" ? record.billing_group_name : "User wallet"}
          secondary={
            record.token_balance_scope === "group"
              ? `${record.personal_tokens_remaining ?? 0} personal tokens`
              : "Direct user usage"
          }
        />
      ),
    },
    {
      title: "Linked Lender",
      key: "linked_lender",
      width: 220,
      render: (_, record) => (
        <MetricCell
          primary={record.lender_account_name || "Not linked"}
          secondary={record.lender_account_email || "Rently uploads disabled"}
        />
      ),
    },
    {
      title: "Granted",
      key: "granted",
      width: 150,
      render: (_, record) => (
        <MetricCell
          primary={`${record.total_tokens_granted ?? 0} lifetime`}
          secondary={`${record.current_month_tokens_granted ?? 0} this month`}
        />
      ),
    },
    {
      title: "Used",
      key: "used",
      width: 150,
      render: (_, record) => (
        <MetricCell
          primary={`${record.total_tokens_used ?? 0} lifetime`}
          secondary={`${record.current_month_tokens_used ?? 0} this month`}
        />
      ),
    },
    {
      title: "Revenue",
      key: "revenue",
      width: 170,
      render: (_, record) => (
        <MetricCell
          primary={formatAmount(record.total_transacted_value)}
          secondary={`${formatAmount(record.current_month_transacted_value)} this month`}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (value) =>
        value ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>,
    },
    {
      title: "Submissions",
      dataIndex: "submissions_count",
      key: "submissions_count",
      width: 120,
    },
    {
      title: "Last Login",
      dataIndex: "last_login_at",
      key: "last_login_at",
      width: 180,
      render: (value) => formatDate(value),
    },
    {
      title: "Actions",
      key: "actions",
      width: 280,
      render: (_, record) => (
        <Space wrap>
          <Button type="primary" ghost onClick={() => openTransactionModal(record)}>
            Add Tokens
          </Button>
          <Button onClick={() => openEdit(record)}>Edit</Button>
          <Button danger onClick={() => deleteUser(record)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const transactionColumns = [
    {
      title: "Date",
      dataIndex: "transacted_at",
      key: "transacted_at",
      width: 180,
      render: (value) => formatDate(value),
    },
    {
      title: "Account",
      key: "user",
      width: 260,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 700, color: "#12304a" }}>{record.portal_user_name}</div>
          <div style={{ fontSize: "12px", color: "#6b7280" }}>{record.portal_user_email}</div>
          {record.balance_scope === "group" ? (
            <div style={{ fontSize: "12px", color: "#0e5aa5", fontWeight: 700 }}>
              Group: {record.billing_group_name}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: "Scope",
      dataIndex: "balance_scope",
      key: "balance_scope",
      width: 100,
      render: (value) =>
        value === "group" ? <Tag color="purple">Group</Tag> : <Tag color="blue">User</Tag>,
    },
    {
      title: "Type",
      dataIndex: "transaction_type",
      key: "transaction_type",
      width: 110,
      render: (value) =>
        value === "credit" ? <Tag color="blue">Credit</Tag> : <Tag color="orange">Usage</Tag>,
    },
    {
      title: "Tokens",
      key: "tokens",
      width: 110,
      render: (_, record) => (
        <span
          style={{
            fontWeight: 800,
            color: record.token_delta > 0 ? "#117949" : "#b42318",
          }}
        >
          {record.token_delta > 0 ? "+" : ""}
          {record.token_delta}
        </span>
      ),
    },
    {
      title: "Value",
      dataIndex: "total_value",
      key: "total_value",
      width: 130,
      render: (value) => formatAmount(value),
    },
    {
      title: "Value / Token",
      dataIndex: "value_per_token",
      key: "value_per_token",
      width: 140,
      render: (value) => formatAmount(value),
    },
    {
      title: "Balance After",
      dataIndex: "balance_after",
      key: "balance_after",
      width: 130,
      render: (value) => <strong>{value}</strong>,
    },
    {
      title: "Reference",
      key: "reference",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 700, color: "#12304a" }}>
            {record.reference_label || "-"}
          </div>
          <div style={{ fontSize: "12px", color: "#6b7280" }}>
            {record.description || "No description"}
          </div>
        </div>
      ),
    },
    {
      title: "Recorded By",
      dataIndex: "admin_user_name",
      key: "admin_user_name",
      width: 180,
      render: (value) => value || "System",
    },
  ];

  const billingGroupColumns = [
    {
      title: "Billing Group",
      dataIndex: "name",
      key: "name",
      render: (value, record) => (
        <div>
          <div style={{ fontWeight: 800, color: "#12304a" }}>{value}</div>
          <div style={{ fontSize: "12px", color: "#6b7280" }}>
            {(record.lender_accounts || []).length} lender member{(record.lender_accounts || []).length === 1 ? "" : "s"}
          </div>
        </div>
      ),
    },
    {
      title: "Lender Members",
      key: "lender_members",
      width: 260,
      render: (_, record) => {
        const lenders = record.lender_accounts || [];
        return lenders.length ? (
          <Space wrap>
            {lenders.map((lender) => (
              <Tag color="purple" key={lender.lender_user_id}>
                {lender.name || lender.email}
              </Tag>
            ))}
          </Space>
        ) : (
          <span style={{ color: "#6b7280" }}>No lenders linked</span>
        );
      },
    },
    {
      title: "Group Tokens",
      dataIndex: "tokens_remaining",
      key: "tokens_remaining",
      width: 140,
      render: (value) => <strong style={{ color: "#0e5aa5" }}>{value}</strong>,
    },
    {
      title: "Granted",
      key: "granted",
      width: 150,
      render: (_, record) => (
        <MetricCell
          primary={`${record.total_tokens_granted ?? 0} lifetime`}
          secondary={`${record.current_month_tokens_granted ?? 0} this month`}
        />
      ),
    },
    {
      title: "Used",
      key: "used",
      width: 150,
      render: (_, record) => (
        <MetricCell
          primary={`${record.total_tokens_used ?? 0} lifetime`}
          secondary={`${record.current_month_tokens_used ?? 0} this month`}
        />
      ),
    },
    {
      title: "Revenue",
      dataIndex: "total_transacted_value",
      key: "total_transacted_value",
      width: 140,
      render: (value) => formatAmount(value),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (value) =>
        value ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 260,
      render: (_, record) => (
        <Space wrap>
          <Button type="primary" ghost onClick={() => openGroupTransactionModal(record)}>
            Add Tokens
          </Button>
          <Button onClick={() => openBillingGroupModal(record)}>Edit</Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={headerRowStyle}>
          <div>
            <h2 style={titleStyle}>Bank Statement Portal Tokens</h2>
            <p style={subtitleStyle}>
              Manage portal users, record token transactions, and track token revenue and utilisation.
            </p>
          </div>
          <Space wrap>
            <Button onClick={() => openBillingGroupModal()}>Create Group</Button>
            <Button onClick={() => setCreateOpen(true)}>Create User</Button>
            <Button type="primary" onClick={() => openTransactionModal()} style={{ background: "#0e5aa5" }}>
              Add Token Transaction
            </Button>
          </Space>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "users",
              label: "Users",
              children: (
                <>
                  <SummaryGrid
                    summary={overviewSummary}
                    mode="overview"
                    titlePrefix="This Month"
                    secondaryTitle="Lifetime Revenue"
                  />
                  <Table
                    rowKey="id"
                    columns={userColumns}
                    dataSource={users}
                    loading={loadingUsers}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1200 }}
                  />
                </>
              ),
            },
            {
              key: "groups",
              label: "Billing Groups",
              children: (
                <Table
                  rowKey="id"
                  columns={billingGroupColumns}
                  dataSource={billingGroups}
                  loading={loadingUsers}
                  pagination={{ pageSize: 10 }}
          scroll={{ x: 1300 }}
                />
              ),
            },
            {
              key: "transactions",
              label: "Transactions",
              children: (
                <>
                  <SummaryGrid
                    summary={transactionSummary}
                    mode="filtered"
                    titlePrefix={selectedPeriodLabel}
                    secondaryTitle="Lifetime Revenue"
                  />

                  <div style={filterBarStyle}>
                    <Input
                      placeholder="Search user, description, or reference"
                      value={transactionFilters.search}
                      onChange={(event) =>
                        setTransactionFilters((current) => ({
                          ...current,
                          search: event.target.value,
                        }))
                      }
                      style={{ minWidth: "240px", flex: 1 }}
                      allowClear
                    />
                    <Select
                      value={transactionFilters.portal_user_id}
                      options={userOptions}
                      onChange={(value) =>
                        setTransactionFilters((current) => ({
                          ...current,
                          portal_user_id: value,
                        }))
                      }
                      style={{ minWidth: "220px" }}
                    />
                    <Select
                      value={transactionFilters.billing_group_id}
                      options={transactionBillingGroupOptions}
                      onChange={(value) =>
                        setTransactionFilters((current) => ({
                          ...current,
                          billing_group_id: value,
                        }))
                      }
                      style={{ minWidth: "190px" }}
                    />
                    <Select
                      value={transactionFilters.balance_scope}
                      options={[
                        { label: "All Scopes", value: "all" },
                        { label: "User Wallet", value: "user" },
                        { label: "Billing Group", value: "group" },
                      ]}
                      onChange={(value) =>
                        setTransactionFilters((current) => ({
                          ...current,
                          balance_scope: value,
                        }))
                      }
                      style={{ minWidth: "160px" }}
                    />
                    <Select
                      value={transactionFilters.transaction_type}
                      options={transactionTypeOptions}
                      onChange={(value) =>
                        setTransactionFilters((current) => ({
                          ...current,
                          transaction_type: value,
                        }))
                      }
                      style={{ minWidth: "150px" }}
                    />
                    <Select
                      value={transactionFilters.month}
                      options={monthOptions}
                      onChange={(value) =>
                        setTransactionFilters((current) => ({
                          ...current,
                          month: value,
                        }))
                      }
                      style={{ minWidth: "150px" }}
                    />
                    <Select
                      value={transactionFilters.year}
                      options={[
                        { label: "All Years", value: "all" },
                        ...availableYears.map((year) => ({
                          label: String(year),
                          value: year,
                        })),
                      ]}
                      onChange={(value) =>
                        setTransactionFilters((current) => ({
                          ...current,
                          year: value,
                        }))
                      }
                      style={{ minWidth: "140px" }}
                    />
                    <Select
                      value={transactionFilters.sort}
                      options={sortOptions}
                      onChange={(value) =>
                        setTransactionFilters((current) => ({
                          ...current,
                          sort: value,
                        }))
                      }
                      style={{ minWidth: "170px" }}
                    />
                  </div>

                  <Table
                    rowKey="id"
                    columns={transactionColumns}
                    dataSource={transactions}
                    loading={loadingTransactions}
                    locale={{
                      emptyText: <Empty description="No token transactions found for these filters." />,
                    }}
                    pagination={{ pageSize: 12 }}
                    scroll={{ x: 1300 }}
                  />
                </>
              ),
            },
          ]}
        />
      </div>

      <Modal
        title="Create Portal User"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          resetCreateForm();
        }}
        onOk={createUser}
        okText="Create"
      >
        <label style={labelStyle}>Name</label>
        <Input
          value={createForm.name}
          onChange={(event) =>
            setCreateForm((current) => ({ ...current, name: event.target.value }))
          }
          placeholder="Portal user name"
        />
        <label style={labelStyle}>Email</label>
        <Input
          value={createForm.email}
          onChange={(event) =>
            setCreateForm((current) => ({ ...current, email: event.target.value }))
          }
          placeholder="Portal user email"
        />
        <label style={labelStyle}>Password</label>
        <Input.Password
          value={createForm.password}
          onChange={(event) =>
            setCreateForm((current) => ({ ...current, password: event.target.value }))
          }
          placeholder="Minimum 8 characters"
        />
        <label style={labelStyle}>Billing Group</label>
        <Select
          value={createForm.billing_group_id}
          options={billingGroupOptions}
          onChange={(value) =>
            setCreateForm((current) => ({ ...current, billing_group_id: value }))
          }
          placeholder="Use personal token wallet"
          style={{ width: "100%" }}
        />
      </Modal>

      <Modal
        title="Edit Portal User"
        open={!!editingUser}
        onCancel={() => setEditingUser(null)}
        onOk={updateUser}
        okText="Save"
      >
        <label style={labelStyle}>Name</label>
        <Input
          value={editForm.name}
          onChange={(event) =>
            setEditForm((current) => ({ ...current, name: event.target.value }))
          }
        />
        <label style={labelStyle}>Email</label>
        <Input
          value={editForm.email}
          onChange={(event) =>
            setEditForm((current) => ({ ...current, email: event.target.value }))
          }
        />
        <label style={labelStyle}>New Password</label>
        <Input.Password
          value={editForm.password}
          onChange={(event) =>
            setEditForm((current) => ({ ...current, password: event.target.value }))
          }
          placeholder="Leave blank to keep current password"
        />
        <label style={labelStyle}>Billing Group</label>
        <Select
          value={editForm.billing_group_id}
          options={billingGroupOptions}
          onChange={(value) =>
            setEditForm((current) => ({ ...current, billing_group_id: value }))
          }
          placeholder="Use personal token wallet"
          style={{ width: "100%" }}
        />
        <div style={{ marginTop: "16px", display: "flex", gap: "10px", alignItems: "center" }}>
          <Switch
            checked={editForm.is_active}
            onChange={(checked) =>
              setEditForm((current) => ({ ...current, is_active: checked }))
            }
          />
          <span style={{ color: "#12304a", fontWeight: 700 }}>Account Active</span>
        </div>
      </Modal>

      <Modal
        title={editingBillingGroup ? "Edit Billing Group" : "Create Billing Group"}
        open={billingGroupOpen}
        onCancel={() => {
          setBillingGroupOpen(false);
          setEditingBillingGroup(null);
          setBillingGroupForm(emptyBillingGroupForm);
        }}
        onOk={saveBillingGroup}
        okText={editingBillingGroup ? "Save" : "Create"}
      >
        <label style={labelStyle}>Group Name</label>
        <Input
          value={billingGroupForm.name}
          onChange={(event) =>
            setBillingGroupForm((current) => ({ ...current, name: event.target.value }))
          }
          placeholder="e.g. ACME Credit Team"
        />
        <label style={labelStyle}>Lender Accounts in Group</label>
        <Select
          mode="multiple"
          value={billingGroupForm.lender_user_ids}
          options={lenderAccountOptions.filter((option) => option.value !== null)}
          onChange={(value) =>
            setBillingGroupForm((current) => ({
              ...current,
              lender_user_ids: value,
            }))
          }
          placeholder="Select lender accounts that should use this billing group"
          showSearch
          optionFilterProp="label"
          style={{ width: "100%" }}
        />
        <div style={{ marginTop: "8px", color: "#6b7280", fontSize: "12px", lineHeight: 1.45 }}>
          Linked lender accounts will use this group token balance for Rently-style Manage Cases document uploads.
        </div>
        <div style={{ marginTop: "16px", display: "flex", gap: "10px", alignItems: "center" }}>
          <Switch
            checked={billingGroupForm.is_active}
            onChange={(checked) =>
              setBillingGroupForm((current) => ({ ...current, is_active: checked }))
            }
          />
          <span style={{ color: "#12304a", fontWeight: 700 }}>Group Active</span>
        </div>
      </Modal>

      <Modal
        title="Record Token Transaction"
        open={transactionOpen}
        onCancel={() => {
          setTransactionOpen(false);
          resetTransactionForm();
        }}
        onOk={createTokenTransaction}
        okText="Record Transaction"
        confirmLoading={transactionSubmitting}
      >
        <label style={labelStyle}>Token Wallet</label>
        <Select
          value={transactionForm.target_type}
          onChange={(value) =>
            setTransactionForm((current) => ({
              ...current,
              target_type: value,
              portal_user_id: value === "group" ? null : current.portal_user_id,
              billing_group_id: value === "user" ? null : current.billing_group_id,
            }))
          }
          options={[
            { label: "User Wallet", value: "user" },
            { label: "Billing Group Wallet", value: "group" },
          ]}
          style={{ width: "100%" }}
        />

        {transactionForm.target_type === "group" ? (
          <>
            <label style={labelStyle}>Billing Group</label>
            <Select
              value={transactionForm.billing_group_id}
              onChange={(value) =>
                setTransactionForm((current) => ({
                  ...current,
                  billing_group_id: value,
                  portal_user_id: null,
                }))
              }
              options={billingGroups.map((group) => ({
                label: `${group.name} (${group.tokens_remaining} tokens)`,
                value: group.id,
              }))}
              placeholder="Select a billing group"
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="label"
            />
            <label style={labelStyle}>Attribution User</label>
            <Select
              value={transactionForm.portal_user_id}
              onChange={(value) =>
                setTransactionForm((current) => ({ ...current, portal_user_id: value }))
              }
              options={targetBillingGroupMembers.map((user) => ({
                label: `${user.name} (${user.email})`,
                value: user.id,
              }))}
              placeholder="Optional; defaults to the first assigned user"
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="label"
              allowClear
            />
          </>
        ) : (
          <>
        <label style={labelStyle}>Portal User</label>
        <Select
          value={transactionForm.portal_user_id}
          onChange={(value) =>
            setTransactionForm((current) => ({ ...current, portal_user_id: value }))
          }
          options={users.map((user) => ({
            label: `${user.name} (${user.email})`,
            value: user.id,
          }))}
          placeholder="Select a portal user"
          style={{ width: "100%" }}
          showSearch
          optionFilterProp="label"
        />
          </>
        )}

        <label style={labelStyle}>Tokens To Add</label>
        <InputNumber
          min={1}
          precision={0}
          value={transactionForm.tokens_amount}
          onChange={(value) =>
            setTransactionForm((current) => ({
              ...current,
              tokens_amount: value ?? 1,
            }))
          }
          style={{ width: "100%" }}
        />

        <div style={{ marginTop: "14px" }}>
          <Checkbox
            checked={transactionForm.is_free}
            onChange={(event) =>
              setTransactionForm((current) => ({
                ...current,
                is_free: event.target.checked,
              }))
            }
          >
            Mark this token grant as free for marketing
          </Checkbox>
        </div>

        <div style={helperBoxStyle}>
          <span>
            Pricing model: first 1,000 lifetime paid tokens at S$0.08, 1,001–19,999 at S$0.06,
            and 20,000+ at S$0.04.
          </span>
          <span>
            Calculated total: <strong>{formatAmount(pricingPreview.totalValue)}</strong>
          </span>
          <span>
            Effective value per token: <strong>{formatAmount(pricingPreview.valuePerToken)}</strong>
          </span>
        </div>

        <label style={labelStyle}>Description</label>
        <Input.TextArea
          value={transactionForm.description}
          onChange={(event) =>
            setTransactionForm((current) => ({
              ...current,
              description: event.target.value,
            }))
          }
          rows={4}
          placeholder={
            transactionForm.is_free
              ? "Required because this token grant is being marked as free."
              : "Optional note for the transaction"
          }
        />
      </Modal>
    </div>
  );
};

const SummaryGrid = ({ summary, titlePrefix, secondaryTitle, mode }) => {
  if (!summary) return null;

  const revenueValue =
    mode === "overview"
      ? summary.current_month_revenue_total
      : summary.filtered_revenue_total;
  const tokensGrantedValue =
    mode === "overview"
      ? summary.current_month_tokens_granted
      : summary.filtered_tokens_granted;
  const tokensUsedValue =
    mode === "overview"
      ? summary.current_month_tokens_used
      : summary.filtered_tokens_used;
  const utilisationValue =
    mode === "overview"
      ? summary.current_month_utilisation_rate
      : summary.filtered_utilisation_rate;

  return (
    <div style={summaryGridStyle}>
      <SummaryCard title={`${titlePrefix} Revenue`} value={formatAmount(revenueValue)} />
      <SummaryCard title={secondaryTitle} value={formatAmount(summary.lifetime_revenue_total)} />
      <SummaryCard title={`${titlePrefix} Tokens Granted`} value={formatCount(tokensGrantedValue)} />
      <SummaryCard title={`${titlePrefix} Tokens Used`} value={formatCount(tokensUsedValue)} />
      <SummaryCard title="Outstanding Tokens" value={formatCount(summary.current_tokens_outstanding)} />
      <SummaryCard title={`${titlePrefix} Utilisation`} value={formatPercentage(utilisationValue)} />
    </div>
  );
};

const SummaryCard = ({ title, value }) => (
  <div style={summaryCardStyle}>
    <Statistic title={title} value={value} valueStyle={{ color: "#12304a", fontSize: "24px", fontWeight: 800 }} />
  </div>
);

const MetricCell = ({ primary, secondary }) => (
  <div>
    <div style={{ fontWeight: 700, color: "#12304a" }}>{primary}</div>
    <div style={{ fontSize: "12px", color: "#6b7280" }}>{secondary}</div>
  </div>
);

const firstValidationError = (payload) => {
  const errorBag = payload?.errors;
  if (!errorBag || typeof errorBag !== "object") return null;

  const firstField = Object.keys(errorBag)[0];
  if (!firstField) return null;

  const messages = errorBag[firstField];
  return Array.isArray(messages) && messages.length > 0 ? messages[0] : null;
};

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd MMM yyyy, hh:mm a");
  } catch (error) {
    return value;
  }
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;

  return numeric.toLocaleString("en-SG", {
    style: "currency",
    currency: "SGD",
  });
};

const formatCount = (value) => {
  const numeric = Number(value || 0);
  return Number.isNaN(numeric) ? "-" : numeric.toLocaleString("en-SG");
};

const formatPercentage = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return "-";
  return `${numeric.toFixed(2)}%`;
};

const calculateTokenPurchaseValue = (lifetimePaidTokens, tokensToAdd) => {
  const tiers = [
    { upTo: 1000, price: 0.08 },
    { upTo: 20000, price: 0.06 },
    { upTo: Infinity, price: 0.04 },
  ];
  let cursor = Number(lifetimePaidTokens || 0);
  let remaining = Number(tokensToAdd || 0);
  let totalValue = 0;

  for (const tier of tiers) {
    if (remaining <= 0) break;
    if (cursor >= tier.upTo) continue;

    const available = Math.min(remaining, tier.upTo - cursor);
    totalValue += available * tier.price;
    cursor += available;
    remaining -= available;
  }

  return {
    totalValue: Number(totalValue.toFixed(2)),
    valuePerToken:
      Number(tokensToAdd || 0) > 0
        ? Number((totalValue / Number(tokensToAdd || 1)).toFixed(4))
        : 0,
  };
};

const pageStyle = {
  height: "calc(100% - 80px)",
  width: "calc(100% - 40px)",
  marginLeft: "20px",
  background:
    "transparent linear-gradient(180deg, #FFFFFFB3 0%, #FFFFFF00 100%) 0% 0% no-repeat padding-box",
  padding: "20px",
  borderRadius: "10px",
  marginTop: "5px",
  color: "black",
  overflowY: "auto",
};

const cardStyle = {
  background: "white",
  borderRadius: "18px",
  padding: "24px",
  boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
};

const headerRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap",
  marginBottom: "20px",
};

const titleStyle = {
  margin: 0,
  fontFamily: "Poppins, sans-serif",
  color: "#12304a",
};

const subtitleStyle = {
  margin: "8px 0 0",
  color: "#6b7280",
};

const filterBarStyle = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
  marginBottom: "18px",
};

const summaryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "14px",
  marginBottom: "20px",
};

const summaryCardStyle = {
  border: "1px solid #e5edf5",
  borderRadius: "14px",
  padding: "16px",
  background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
};

const helperBoxStyle = {
  marginTop: "12px",
  padding: "12px 14px",
  borderRadius: "10px",
  background: "#f5f8fc",
  color: "#4b5563",
  fontSize: "13px",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
};

const labelStyle = {
  display: "block",
  marginTop: "14px",
  marginBottom: "6px",
  fontWeight: 700,
  color: "#12304a",
};

export default PortalUsersAdmin;
